import React, { useState, useEffect } from 'react';
import { Shield, Save, Loader2, Building2, Banknote, FileText, AlertTriangle, Scale, TrendingDown, Calendar, CheckCircle2, XCircle, Sparkles, Download, ClipboardCheck } from 'lucide-react';
import { crmApi } from '../../services/crmApi';

const TABS = [
  { id: 'company', label: 'Firma / Dłużnik', icon: Building2, color: 'text-blue-600' },
  { id: 'debt', label: 'Zadłużenie', icon: AlertTriangle, color: 'text-red-600' },
  { id: 'finances', label: 'Sytuacja finansowa', icon: TrendingDown, color: 'text-amber-600' },
  { id: 'solution', label: 'Rozwiązanie', icon: Scale, color: 'text-emerald-600' },
  { id: 'notes', label: 'Notatki & Dokumenty', icon: FileText, color: 'text-sky-600' },
] as const;

type TabId = typeof TABS[number]['id'];

const DEBT_TYPES: Record<string, string> = {
  skladki_społeczne: 'Składki społeczne',
  skladki_zdrowotne: 'Składki zdrowotne',
  fundusz_pracy: 'Fundusz Pracy',
  fgsp: 'FGŚP',
  wszystkie: 'Wszystkie składki',
  inne: 'Inne',
};

const SOLUTIONS: Record<string, string> = {
  rozlozenie_na_raty: 'Rozłożenie na raty',
  odroczenie_terminu: 'Odroczenie terminu płatności',
  umorzenie: 'Umorzenie należności',
  uklad_ratalny: 'Układ ratalny',
  inne: 'Inne',
};

const BUSINESS_STATUSES: Record<string, string> = {
  active: 'Działalność aktywna',
  suspended: 'Działalność zawieszona',
  closed: 'Działalność zamknięta',
  employment: 'Zatrudnienie (umowa)',
  unemployed: 'Bezrobotny',
  pension: 'Emeryt / rencista',
};

interface Props {
  application: any;
  onSaved: () => void;
}

export const ZUSDetailSection: React.FC<Props> = ({ application, onSaved }) => {
  const app = application;
  const [activeTab, setActiveTab] = useState<TabId>('company');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [fields, setFields] = useState<Record<string, any>>({});
  const [aiLoading, setAiLoading] = useState<string | null>(null);
  const [completenessResult, setCompletenessResult] = useState<string | null>(null);

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const runAI = async (action: string) => {
    setAiLoading(action);
    try {
      if (action === 'debt-analysis') {
        const blob = await crmApi.zusDebtAnalysis(app.id);
        downloadBlob(blob, `Analiza_ZUS_${app.first_name}_${app.last_name}.txt`);
      } else if (action === 'negotiation') {
        const blob = await crmApi.zusNegotiationStrategy(app.id);
        downloadBlob(blob, `Strategia_ZUS_${app.first_name}_${app.last_name}.txt`);
      } else if (action === 'letter') {
        const blob = await crmApi.zusApplicationLetter(app.id, fields.zus_requested_solution);
        downloadBlob(blob, `Wniosek_ZUS_${app.first_name}_${app.last_name}.txt`);
      } else if (action === 'completeness') {
        const result = await crmApi.zusCompletenessCheck(app.id);
        setCompletenessResult(result.analysis);
      }
    } catch (e) {
      console.error(`ZUS AI action ${action} failed:`, e);
    } finally {
      setAiLoading(null);
    }
  };

  useEffect(() => {
    if (!app) return;
    const f: Record<string, any> = {};
    Object.keys(app).forEach(k => {
      if (k.startsWith('zus_')) f[k] = app[k];
    });
    // Also pull notes and operator_notes
    f._notes = app.notes || '';
    f._operator_notes = app.operator_notes || '';
    f._nip = app.nip || '';
    f._business_name = app.business_name || '';
    setFields(f);
    setDirty(false);
  }, [app?.id]);

  const set = (key: string, value: any) => {
    setFields(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: Record<string, any> = {};
      Object.entries(fields).forEach(([k, v]) => {
        if (k.startsWith('zus_')) payload[k] = v;
      });
      // Also save general fields
      payload.notes = fields._notes;
      payload.operator_notes = fields._operator_notes;
      if (fields._nip) payload.nip = fields._nip;
      if (fields._business_name) payload.business_name = fields._business_name;

      await crmApi.updateApplication(app.id, payload);
      setDirty(false);
      onSaved();
    } catch (e) {
      console.error('Save ZUS detail failed:', e);
    } finally {
      setSaving(false);
    }
  };

  // Debt summary for header
  const debtAmount = parseFloat(fields.zus_debt_amount) || 0;
  const monthlyIncome = parseFloat(fields.zus_monthly_income) || 0;
  const monthlyExpenses = parseFloat(fields.zus_monthly_expenses) || 0;
  const monthlyBalance = monthlyIncome - monthlyExpenses;
  const debtToIncomeRatio = monthlyIncome > 0 ? (debtAmount / (monthlyIncome * 12) * 100) : 0;

  const Field = ({ label, children, span = 1 }: { label: string; children: React.ReactNode; span?: number }) => (
    <div className={span === 2 ? 'col-span-2' : ''}>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );

  const Input = ({ value, onChange, type = 'text', placeholder = '' }: { value: string; onChange: (v: string) => void; type?: string; placeholder?: string }) => (
    <input
      type={type}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    />
  );

  const Select = ({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: Record<string, string> }) => (
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    >
      <option value="">— wybierz —</option>
      {Object.entries(options).map(([k, v]) => (
        <option key={k} value={k}>{v}</option>
      ))}
    </select>
  );

  const Textarea = ({ value, onChange, rows = 3, placeholder = '' }: { value: string; onChange: (v: string) => void; rows?: number; placeholder?: string }) => (
    <textarea
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    />
  );

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header with debt summary */}
      <div className="bg-gradient-to-r from-red-50 to-amber-50 px-6 py-4 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield size={22} className="text-red-600" />
            <h3 className="text-lg font-bold text-gray-900">Ulgi ZUS — szczegóły wniosku</h3>
          </div>
          <button
            onClick={handleSave}
            disabled={!dirty || saving}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              dirty ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Zapisz
          </button>
        </div>

        {/* Quick KPIs */}
        {debtAmount > 0 && (
          <div className="mt-3 flex gap-4 text-sm">
            <div className="bg-white/70 rounded-lg px-3 py-1.5">
              <span className="text-gray-500">Dług: </span>
              <span className="font-bold text-red-700">{debtAmount.toLocaleString('pl-PL')} zł</span>
            </div>
            <div className="bg-white/70 rounded-lg px-3 py-1.5">
              <span className="text-gray-500">Bilans mies.: </span>
              <span className={`font-bold ${monthlyBalance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                {monthlyBalance.toLocaleString('pl-PL')} zł
              </span>
            </div>
            {debtToIncomeRatio > 0 && (
              <div className="bg-white/70 rounded-lg px-3 py-1.5">
                <span className="text-gray-500">Dług/roczny dochód: </span>
                <span className={`font-bold ${debtToIncomeRatio > 50 ? 'text-red-700' : debtToIncomeRatio > 25 ? 'text-amber-700' : 'text-emerald-700'}`}>
                  {debtToIncomeRatio.toFixed(0)}%
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b bg-gray-50 overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${
                active ? `${tab.color} border-current bg-white` : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="p-6">
        {/* ===== Tab: Firma / Dłużnik ===== */}
        {activeTab === 'company' && (
          <div className="grid grid-cols-2 gap-4">
            <Field label="Nazwa firmy / Imię i nazwisko">
              <Input value={fields.zus_company_name || fields._business_name} onChange={v => set('zus_company_name', v)} placeholder="Firma lub osoba fizyczna" />
            </Field>
            <Field label="NIP">
              <Input value={fields.zus_company_nip || fields._nip} onChange={v => set('zus_company_nip', v)} placeholder="000-000-00-00" />
            </Field>
            <Field label="Status działalności">
              <Select value={fields.zus_business_status} onChange={v => set('zus_business_status', v)} options={BUSINESS_STATUSES} />
            </Field>
            <Field label="PESEL">
              <Input value={app.pesel || ''} onChange={() => {}} placeholder="—" />
            </Field>
            <Field label="Telefon">
              <Input value={app.phone || ''} onChange={() => {}} />
            </Field>
            <Field label="Email">
              <Input value={app.email || ''} onChange={() => {}} />
            </Field>
          </div>
        )}

        {/* ===== Tab: Zadłużenie ===== */}
        {activeTab === 'debt' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Typ zadłużenia">
                <Select value={fields.zus_debt_type} onChange={v => set('zus_debt_type', v)} options={DEBT_TYPES} />
              </Field>
              <Field label="Kwota zadłużenia (zł)">
                <Input value={fields.zus_debt_amount} onChange={v => set('zus_debt_amount', v)} type="number" placeholder="0" />
              </Field>
              <Field label="Okres zadłużenia" span={2}>
                <Input value={fields.zus_debt_period} onChange={v => set('zus_debt_period', v)} placeholder="np. 01/2023 – 12/2025" />
              </Field>
            </div>

            {/* Debt severity indicator */}
            {debtAmount > 0 && (
              <div className={`rounded-lg p-4 ${debtAmount > 50000 ? 'bg-red-50 border border-red-200' : debtAmount > 10000 ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {debtAmount > 50000 ? (
                    <XCircle size={18} className="text-red-600" />
                  ) : debtAmount > 10000 ? (
                    <AlertTriangle size={18} className="text-amber-600" />
                  ) : (
                    <CheckCircle2 size={18} className="text-green-600" />
                  )}
                  <span className="font-medium text-sm">
                    {debtAmount > 50000 ? 'Wysoki dług — rekomendowane umorzenie lub układ ratalny' :
                     debtAmount > 10000 ? 'Średni dług — możliwe rozłożenie na raty' :
                     'Niski dług — szansa na szybkie rozwiązanie'}
                  </span>
                </div>
                <div className="text-xs text-gray-600">
                  {debtAmount > 50000 && 'Przy długu powyżej 50 000 zł warto rozważyć wniosek o umorzenie (art. 28 ustawy o sus). '}
                  {debtToIncomeRatio > 50 && 'Stosunek długu do dochodu rocznego przekracza 50% — silny argument za ulgą.'}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== Tab: Sytuacja finansowa ===== */}
        {activeTab === 'finances' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Dochód miesięczny (zł)">
                <Input value={fields.zus_monthly_income} onChange={v => set('zus_monthly_income', v)} type="number" placeholder="0" />
              </Field>
              <Field label="Wydatki miesięczne (zł)">
                <Input value={fields.zus_monthly_expenses} onChange={v => set('zus_monthly_expenses', v)} type="number" placeholder="0" />
              </Field>
            </div>

            <Field label="Opis sytuacji finansowej" span={2}>
              <Textarea
                value={fields.zus_financial_situation}
                onChange={v => set('zus_financial_situation', v)}
                rows={4}
                placeholder="Opisz sytuację finansową: źródła dochodu, stałe wydatki, osoby na utrzymaniu, nieruchomości, pojazdy, inne zobowiązania..."
              />
            </Field>

            {/* Financial summary card */}
            {(monthlyIncome > 0 || monthlyExpenses > 0) && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Podsumowanie finansowe</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-emerald-600">{monthlyIncome.toLocaleString('pl-PL')} zł</div>
                    <div className="text-xs text-gray-500">Dochód / mies.</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-red-600">{monthlyExpenses.toLocaleString('pl-PL')} zł</div>
                    <div className="text-xs text-gray-500">Wydatki / mies.</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-lg font-bold ${monthlyBalance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {monthlyBalance.toLocaleString('pl-PL')} zł
                    </div>
                    <div className="text-xs text-gray-500">Bilans / mies.</div>
                  </div>
                </div>
                {debtAmount > 0 && monthlyBalance > 0 && (
                  <div className="mt-3 text-center text-xs text-gray-500">
                    Przy obecnym bilansie spłata długu ({debtAmount.toLocaleString('pl-PL')} zł) zajmie ok. <strong>{Math.ceil(debtAmount / monthlyBalance)} mies.</strong>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ===== Tab: Rozwiązanie ===== */}
        {activeTab === 'solution' && (
          <div className="space-y-4">
            <Field label="Wnioskowane rozwiązanie">
              <Select value={fields.zus_requested_solution} onChange={v => set('zus_requested_solution', v)} options={SOLUTIONS} />
            </Field>

            {fields.zus_requested_solution && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">
                  {SOLUTIONS[fields.zus_requested_solution] || fields.zus_requested_solution}
                </h4>
                <div className="text-xs text-blue-700 space-y-1">
                  {fields.zus_requested_solution === 'rozlozenie_na_raty' && (
                    <>
                      <p>Podstawa prawna: art. 29 ustawy o systemie ubezpieczeń społecznych</p>
                      <p>Wymagane: wniosek RSR (ZUS) + dokumenty o sytuacji finansowej</p>
                      <p>ZUS rozpatruje w ciągu 2 miesięcy. Opłata prolongacyjna = 50% stawki odsetek.</p>
                    </>
                  )}
                  {fields.zus_requested_solution === 'odroczenie_terminu' && (
                    <>
                      <p>Podstawa prawna: art. 29 ustawy o sus</p>
                      <p>Odroczenie = przesunięcie terminu płatności bieżących składek</p>
                      <p>Opłata prolongacyjna naliczana za okres odroczenia.</p>
                    </>
                  )}
                  {fields.zus_requested_solution === 'umorzenie' && (
                    <>
                      <p>Podstawa prawna: art. 28 ustawy o sus</p>
                      <p>Możliwe tylko przy całkowitej nieściągalności LUB trudnej sytuacji (art. 28 ust. 3a)</p>
                      <p>Wymagane: RSU, dokumenty finansowe, oświadczenie o stanie majątkowym</p>
                    </>
                  )}
                  {fields.zus_requested_solution === 'uklad_ratalny' && (
                    <>
                      <p>Układ ratalny = indywidualny harmonogram spłat</p>
                      <p>ZUS może zaproponować od 6 do 60 rat. Opłata prolongacyjna 50% stawki odsetek.</p>
                      <p>Brak terminowej spłaty = rozwiązanie układu i egzekucja.</p>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* AI Actions */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Sparkles size={16} className="text-purple-500" />
                Narzędzia AI
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => runAI('debt-analysis')}
                  disabled={!!aiLoading}
                  className="flex items-center gap-2 px-3 py-2.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-sm font-medium transition-colors border border-red-200"
                >
                  {aiLoading === 'debt-analysis' ? <Loader2 size={16} className="animate-spin" /> : <TrendingDown size={16} />}
                  Analiza zadłużenia
                </button>
                <button
                  onClick={() => runAI('negotiation')}
                  disabled={!!aiLoading}
                  className="flex items-center gap-2 px-3 py-2.5 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-lg text-sm font-medium transition-colors border border-amber-200"
                >
                  {aiLoading === 'negotiation' ? <Loader2 size={16} className="animate-spin" /> : <Scale size={16} />}
                  Strategia negocjacji
                </button>
                <button
                  onClick={() => runAI('letter')}
                  disabled={!!aiLoading}
                  className="flex items-center gap-2 px-3 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-sm font-medium transition-colors border border-blue-200"
                >
                  {aiLoading === 'letter' ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                  Generuj wniosek do ZUS
                </button>
                <button
                  onClick={() => runAI('completeness')}
                  disabled={!!aiLoading}
                  className="flex items-center gap-2 px-3 py-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-sm font-medium transition-colors border border-emerald-200"
                >
                  {aiLoading === 'completeness' ? <Loader2 size={16} className="animate-spin" /> : <ClipboardCheck size={16} />}
                  Sprawdź kompletność
                </button>
              </div>

              {completenessResult && (
                <div className="mt-4 bg-gray-50 rounded-lg p-4 border">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="text-sm font-medium text-gray-700">Wynik sprawdźenia kompletności</h5>
                    <button onClick={() => setCompletenessResult(null)} className="text-gray-400 hover:text-gray-600">
                      <XCircle size={16} />
                    </button>
                  </div>
                  <pre className="text-xs text-gray-600 whitespace-pre-wrap font-sans">{completenessResult}</pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== Tab: Notatki & Dokumenty ===== */}
        {activeTab === 'notes' && (
          <div className="space-y-4">
            <Field label="Notatki klienta (z formularza)" span={2}>
              <Textarea
                value={fields._notes}
                onChange={v => set('_notes', v)}
                rows={4}
                placeholder="Notatki klienta..."
              />
            </Field>
            <Field label="Notatki operatorskie (wewnętrzne)" span={2}>
              <Textarea
                value={fields._operator_notes}
                onChange={v => set('_operator_notes', v)}
                rows={4}
                placeholder="Notatki operatora — widoczne tylko w CRM..."
              />
            </Field>
          </div>
        )}
      </div>
    </div>
  );
};
